import { createSlice } from '@reduxjs/toolkit'

import { USER_PROVISIONING_STEPS } from './constants'
import { calculateStep, mapAttributeValues } from './helpers'
import {
  destroyBearerToken,
  destroyScimConfiguration,
  fetchAvailableAttributeValues,
  fetchPreviewInformation,
  fetchScimSettings,
  fetchUserGroupsAndGroupMappings,
  generateBearerToken,
  saveGroupScimConfiguration,
  toggleConfiguration,
  toggleGroupMappingState
} from './thunks'

const initialState = {
  attributeValues: [],
  currentStep: USER_PROVISIONING_STEPS.SCIM_PROVISIONING,
  groupMappings: [],
  isDeactivationPopupDisplayed: false,
  isDeleteScimPopupDisplayed: false,
  isLoading: false,
  isPreviewChangesDisplayed: false,
  isPreviewInformationLoading: false,
  isScimConfigurationSaving: false,
  isUserGroupsLoading: true,
  newUserGroups: [],
  previewResponse: [],
  scimSettings: {},
  userGroups: [],
}

const slice = createSlice({
  name: 'userProvisioning',
  initialState,
  reducers: {
    addAttributeValue: (state, { payload }) => {
      state.attributeValues.push(payload)
    },
    addNewUserGroup: (state, action) => {
      state.newUserGroups.push(action.payload)
    },
    addUserGroup: (state, { payload }) => {
      const userGroupIndex = state.userGroups.findIndex(({ id }) => id === payload.id)
      if (userGroupIndex === -1) {
        state.userGroups.push(payload)
      }
    },
    displayDeactivationPopup: (state) => {
      state.isDeactivationPopupDisplayed = true
    },
    editNewGroupName: (state, action) => {
      state.newUserGroups = state.newUserGroups.map((group) => {
        if (group.id === action.payload.id) {
          return action.payload
        }

        return group
      })
    },
    hideDeactivationPopup: (state) => {
      state.isDeactivationPopupDisplayed = false
    },
    hidePreviewChangesModal: (state) => {
      state.isPreviewChangesDisplayed = false
      state.previewResponse = []
    },
    removeAttributeValue: (state, { payload: id }) => {
      state.attributeValues = [...state.attributeValues].filter(({ id: attrValueId }) => id !== attrValueId)
    },
    setCurrentStep: (state, { payload }) => {
      state.currentStep = payload
    },
    setIsDeleteScimPopupDisplayed: (state, { payload }) => {
      state.isDeleteScimPopupDisplayed = payload
    },
    updateAttributeValue: (state, { payload }) => {
      const { index, values } = payload
      const newAttributeCollection = [...state.attributeValues]

      newAttributeCollection[index] = { ...newAttributeCollection[index], ...values }

      state.attributeValues = newAttributeCollection
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchUserGroupsAndGroupMappings.pending, (state) => {
      state.isUserGroupsLoading = true
      state.userGroups = []
    })
    builder.addCase(fetchUserGroupsAndGroupMappings.fulfilled, (state, { payload: { userGroupsResponse, groupMappingsResponse } }) => {
      state.isUserGroupsLoading = false
      state.groupMappings = groupMappingsResponse
      const collection = userGroupsResponse.collection
      const attributeValueSet = new Set(state.attributeValues.map((attributeValue) => attributeValue.name))
      // Add user groups from mappings to user groups collection
      groupMappingsResponse.forEach((groupMapping) => {
        attributeValueSet.add(groupMapping.attribute_value)
        const userGroupIndex = collection.findIndex(({ id }) => id === groupMapping.user_group.id)
        if (userGroupIndex > -1) {
          return
        }
        collection.push(groupMapping.user_group)
      })
      state.userGroups = collection
      state.attributeValues = mapAttributeValues({
        groupMappings: groupMappingsResponse,
        attributeValues: state.attributeValues || []
      }, [...attributeValueSet])
    })
    builder.addCase(fetchUserGroupsAndGroupMappings.rejected, (state) => {
      state.isUserGroupsLoading = false
      state.userGroups = []
    })
    builder.addCase(saveGroupScimConfiguration.pending, (state) => {
      state.isScimConfigurationSaving = true
    })
    builder.addCase(saveGroupScimConfiguration.fulfilled, (state, { payload }) => {
      state.isScimConfigurationSaving = false
      state.isPreviewChangesDisplayed = false
      state.newUserGroups = []
      state.attributeValues = []
      state.scimSettings = { ...payload }
    })
    builder.addCase(saveGroupScimConfiguration.rejected, (state) => {
      state.isScimConfigurationSaving = false
    })
    builder.addCase(fetchPreviewInformation.pending, (state) => {
      state.isPreviewInformationLoading = true
    })
    builder.addCase(fetchPreviewInformation.fulfilled, (state, { payload: { previewResponse } }) => {
      state.isPreviewChangesDisplayed = true
      state.isPreviewInformationLoading = false
      state.previewResponse = previewResponse
    })
    builder.addCase(fetchPreviewInformation.rejected, (state) => {
      state.isPreviewInformationLoading = false
    })
    builder.addCase(fetchAvailableAttributeValues.fulfilled, (state, { payload }) => {
      state.attributeValues = mapAttributeValues(state, payload)
    })
    builder.addCase(fetchScimSettings.pending, (state) => {
      state.isLoading = true
    })
    builder.addCase(fetchScimSettings.fulfilled, (state, { payload }) => {
      state.currentStep = calculateStep(payload)
      state.isLoading = false
      state.scimSettings = { ...payload }
    })
    builder.addCase(fetchScimSettings.rejected, (state) => {
      state.scimSettings = initialState.scimSettings
      state.isLoading = false
    })
    builder.addCase(destroyScimConfiguration.pending, (state) => {
      state.isLoading = true
    })
    builder.addCase(destroyScimConfiguration.fulfilled, (state) => {
      state.scimSettings = initialState.scimSettings
      state.currentStep = initialState.currentStep
      state.isDeleteScimPopupDisplayed = false
      state.isLoading = false
    })
    builder.addCase(destroyScimConfiguration.rejected, (state) => {
      state.isLoading = false
    })
    builder.addCase(toggleConfiguration.pending, (state) => {
      state.isLoading = true
    })
    builder.addCase(toggleConfiguration.fulfilled, (state, { payload }) => {
      state.scimSettings = { ...payload }
      state.isLoading = false
    })
    builder.addCase(toggleConfiguration.rejected, (state) => {
      state.isLoading = false
    })
    builder.addCase(generateBearerToken.fulfilled, (state, { payload }) => {
      state.scimSettings = {
        ...state.scimSettings,
        bearer_tokens: [...state.scimSettings.bearer_tokens, payload],
      }
    })
    builder.addCase(destroyBearerToken.fulfilled, (state, { payload }) => {
      state.scimSettings = {
        ...state.scimSettings,
        bearer_tokens: state.scimSettings.bearer_tokens.filter((token) => token.id !== payload.id)
      }
    })
    builder.addCase(toggleGroupMappingState.fulfilled, (state, { payload }) => {
      state.scimSettings = { ...payload }
    })
  }
})

export const { reducer } = slice

export const {
  addAttributeValue,
  addNewUserGroup,
  addUserGroup,
  displayDeactivationPopup,
  editNewGroupName,
  hideDeactivationPopup,
  hidePreviewChangesModal,
  removeAttributeValue,
  setCurrentStep,
  setIsDeleteScimPopupDisplayed,
  updateAttributeValue,
} = slice.actions
